import {Tags} from 'Event/attendee'
import React, {useCallback, useMemo, useState} from 'react'
import AttendeeProfileProvider from 'Event/attendee-rules/AttendeeProfileProvider'
import {useEvent} from 'Event/EventProvider'

export type Group = {
  key: string
  value: string
}

interface PreviewAttendeeProfileContextProps {
  groups: Group[]
  tags: Tags
  firstName: string
  lastName: string
  email: string
  setGroups: (groups: Group[]) => void
  setTags: (tags: Tags) => void
  setFirstName: (firstName: string) => void
  setLastName: (lastName: string) => void
  setEmail: (email: string) => void
  save: (profile: {
    groups: Group[]
    tags: Tags
    firstName: string
    lastName: string
    email: string
  }) => void
}

const PreviewAttendeeProfileContext = React.createContext(
  (undefined as unknown) as PreviewAttendeeProfileContextProps,
)

export default function PreviewAttendeeProfileProvider(props: {
  children: React.ReactNode
}) {
  const {
    groups: savedGroups,
    tags: savedTags,
    firstName: savedFirstName,
    lastName: savedLastName,
    email: savedEmail,
    save,
  } = useSavedProfile()

  const [groups, setGroups] = useState<Group[]>(savedGroups)
  const [tags, setTags] = useState<Tags>(savedTags)
  const [firstName, setFirstName] = useState(savedFirstName)
  const [lastName, setLastName] = useState(savedLastName)
  const [email, setEmail] = useState(savedEmail)

  const attendeeGroups = useMemo(
    () =>
      groups.reduce((acc, group) => {
        acc[group.key] = group.value
        return acc
      }, {} as any),
    [groups],
  )

  const profile = useMemo(
    () => ({
      groups,
      tags,
      firstName,
      lastName,
      email,
      setGroups,
      setTags,
      setFirstName,
      setLastName,
      setEmail,
      save,
    }),
    [groups, tags, firstName, lastName, email, save],
  )

  return (
    <PreviewAttendeeProfileContext.Provider value={profile}>
      <AttendeeProfileProvider groups={attendeeGroups} tags={tags}>
        {props.children}
      </AttendeeProfileProvider>
    </PreviewAttendeeProfileContext.Provider>
  )
}

export function usePreviewAttendeeProfile() {
  const context = React.useContext(PreviewAttendeeProfileContext)
  if (context === undefined) {
    throw new Error(
      'usePreviewAttendeeProfile must be used within a PreviewAttendeeProfileProvider',
    )
  }

  return context
}

export function useOptionalPreviewAttendeeProfile():
  | PreviewAttendeeProfileContextProps
  | undefined {
  return React.useContext(PreviewAttendeeProfileContext)
}

function useSavedProfile() {
  const {event} = useEvent()
  const tagsKey = `__obvio_${event.id}_preview_tags__`
  const groupsKey = `__obvio_${event.id}_preview_groups__`
  const firstNameKey = `__obvio_${event.id}_preview_first_name__`
  const lastNameKey = `__obvio_${event.id}_preview_last_name__`
  const emailKey = `__obvio_${event.id}_preview_email__`

  const savedGroups = localStorage.getItem(groupsKey)
  const savedTags = localStorage.getItem(tagsKey)

  const save = useCallback(
    (profile: {
      groups: Group[]
      tags: Tags
      firstName: string
      lastName: string
      email: string
    }) => {
      localStorage.setItem(groupsKey, JSON.stringify(profile.groups))
      localStorage.setItem(tagsKey, JSON.stringify(profile.tags))
      localStorage.setItem(firstNameKey, profile.firstName)
      localStorage.setItem(lastNameKey, profile.lastName)
      localStorage.setItem(emailKey, profile.email)
    },
    [groupsKey, tagsKey, firstNameKey, lastNameKey, emailKey],
  )

  return {
    groups: savedGroups ? JSON.parse(savedGroups) : [],
    tags: savedTags ? JSON.parse(savedTags) : [],
    firstName: localStorage.getItem(firstNameKey) ?? '',
    lastName: localStorage.getItem(lastNameKey) ?? '',
    email: localStorage.getItem(emailKey) ?? '',
    save,
  }
}
